/**
 * Per-fire FireSTARR run state — #929 M5.
 *
 * Thin fetch of the rows `firestarr_runs` recorded for one fire, so the Models
 * tab can answer "why is this timeframe blank?" from the database rather than
 * from a container log that has already rotated.
 *
 * Shaped after `useLifecycleData`: `getBackendUrl()`, a credentialed fetch, and
 * a cancelled flag so a response arriving after unmount does not set state.
 */
import { useState, useEffect } from 'react';
import { WildfireEntity } from '../types/wildfire';
import { getBackendUrl } from '../services/configService';

/**
 * Mirrors `RUN_STATUSES` in `src/backend/services/firestarr/runStore.ts`.
 *
 * Retyped rather than imported because the frontend must not import from
 * `src/backend`. That makes it a duplicated contract, so it is named as one
 * here: if the backend's four statuses ever change, this is the other place.
 */
export type FirestarrRunStatus = 'pending' | 'processing' | 'complete' | 'failed';

/**
 * The seven projection horizons, matching the national FireSTARR layers.
 *
 * Mirrors `FIRESTARR_TIMEFRAMES` in
 * `src/backend/services/firestarr/runStore.ts`, retyped for the same reason as
 * the statuses above: the frontend must not import from `src/backend`. Named as
 * a duplicated contract so the other place is findable.
 *
 * The panel renders ALL of these whether or not a row exists for them — an
 * absent horizon is the answer to "why is Day 7 blank", and omitting it
 * repeats the silence the map already gave.
 */
export const FIRESTARR_TIMEFRAMES = [1, 2, 3, 6, 7, 13, 14] as const;

/**
 * One row of `firestarr_runs` (migration 047) as the panel needs it.
 *
 * NOT the backend's `FirestarrRun` interface, which carries only the nine
 * columns `CLAIM_SQL` returns — `started_at` and `completed_at` are absent
 * there. The panel needs the timestamps, so this is a second, wider row type
 * rather than a widening of the one the atomic claim depends on.
 */
export interface FirestarrRunView {
    id: string;
    wildfire_id: number;
    fire_number: string;
    /** Projection horizon in days: 1, 2, 3, 6, 7, 13 or 14. */
    timeframe_days: number;
    /** The NWT calendar day this run belongs to, not the UTC day. */
    run_date: string;
    status: FirestarrRunStatus;
    nomad_model_id: string | null;
    nomad_job_id: string | null;
    /** Nomad's real status and body on failure. Null unless `status` is 'failed'. */
    error: string | null;
    started_at: string | null;
    completed_at: string | null;
}

export interface UseFirestarrRunsResult {
    /**
     * The runs recorded for this fire, newest run_date first.
     *
     * An EMPTY array is a real answer and not an error: it means no run was
     * ever scheduled for this fire — it may be excluded by the harness's
     * exclusion list, or the night may predate the harness being enabled. The
     * panel must render that differently from `pending`, or it reintroduces
     * the blank timeframe M5 exists to remove.
     */
    runs: FirestarrRunView[];
    loading: boolean;
    /** A failure to LOAD the runs — not a failed run. The two are unrelated. */
    error: string | null;
}

export function useFirestarrRuns(wildfire: WildfireEntity): UseFirestarrRunsResult {
    const [state, setState] = useState<UseFirestarrRunsResult>({
        runs: [],
        loading: true,
        error: null,
    });

    const fireNumber = wildfire.fire_number;

    useEffect(() => {
        let cancelled = false;

        async function load(): Promise<void> {
            try {
                // Encoded: fire numbers carry a hyphen today, but the route key
                // is free text and must not be assumed URL-safe.
                const url = `${getBackendUrl()}/api/wildfires/${encodeURIComponent(fireNumber)}/firestarr-runs`;
                // Credentialed: the route is gated on wildfires.read, and an
                // anonymous request is refused — which would surface as a load
                // error on every fire for a reason nothing on screen explains.
                const response = await fetch(url, { credentials: 'include' });

                if (!response.ok) {
                    throw new Error(
                        `FireSTARR run state request failed with ${response.status}`,
                    );
                }

                const runs = (await response.json()) as FirestarrRunView[];
                if (cancelled) return;
                setState({ runs, loading: false, error: null });
            } catch (error) {
                if (cancelled) return;
                // A failed LOAD is recorded as an error and NEVER as an empty
                // list: an empty list means "nothing was ever scheduled", and
                // borrowing that meaning would tell a duty officer the harness
                // skipped the fire when in fact nobody knows.
                setState({
                    runs: [],
                    loading: false,
                    error: error instanceof Error ? error.message : String(error),
                });
            }
        }

        void load();

        return () => {
            cancelled = true;
        };
    }, [fireNumber]);

    return state;
}
